import { CollapsibleCard } from '@ctw/shared/components/containers/collapsible-card';
import {
  StreamingContent,
  type StreamingContentProps,
} from '@ctw/shared/components/containers/streaming-content';
import { notify } from '@ctw/shared/components/toast';
import type { usePatientSummaries } from '@ctw/shared/content/summarization/use-patient-summaries';
import {
  fetchCanonicalCitationResource,
  useFqsHotstorageRequestOptions,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { isFetchableResourceType } from '@ctw/shared/utils/fhir/resource-mappings';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useMemo } from 'react';

interface PatientSummaryCardProps {
  enabled: boolean;
  summaryResponse: ReturnType<typeof usePatientSummaries>;
  summaryIndex: number;
  streaming: boolean;
  onAllContentPresented: StreamingContentProps['onAllContentPresented'];
}
export const PatientSummaryCard = memo(
  ({
    enabled,
    summaryResponse,
    summaryIndex,
    streaming,
    onAllContentPresented,
  }: PatientSummaryCardProps) => {
    const { ctwFetch, graphqlClient, requestContext } = useCTW();
    const { patient } = usePatientContext();
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();
    const { openResourceDrawer } = useResourceDrawer();
    const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();

    const summary = summaryResponse.summaries[summaryIndex];

    const contentBlocks = useMemo(
      () => [
        {
          tokenizationMode: 'word' as const,
          markdown: summary.content,
          streaming,
        },
      ],
      [summary.content, streaming],
    );

    const handleCitation = useCallback(
      (citationId: string) => {
        const citation = summary.citations?.find(
          (citation) => citation.id.toString() === citationId,
        );

        if (!citation) {
          return {
            onClick: () =>
              notify({
                type: 'error',
                title: 'Unable to load citation',
                body: 'We were unable to load this citation (code: CNF). If this problem persists, please contact support.',
              }),
          };
        }

        return {
          onClick: () => {
            if (!isFetchableResourceType(citation.resourceType)) {
              notify({
                type: 'error',
                title: 'Unable to load citation',
                body: 'We were unable to load this citation (code: IRT). If this problem persists, please contact support.',
              });
            } else {
              telemetry.trackInteraction('patient_summary_citation_link_click', {
                summaryJobId: summaryResponse.summaryJobId,
                summaryJobStatus: summaryResponse.status,
                summaryStatus: summary.status,
                summaryIndex,
                citationId: citation.id,
                resourceType: citation.resourceType,
                resourceId: citation.resourceId,
              });

              void fetchCanonicalCitationResource({
                resourceType: citation.resourceType,
                resourceIds: [citation.resourceId],
                telemetry,
                ctwFetch,
                patient,
                queryClient,
                graphqlClient,
                requestContext,
                fqsHotstorageRequestOptions,
              }).then((resource) => {
                if (resource === null) {
                  notify({
                    type: 'error',
                    title: 'Unable to load citation',
                    body: 'We were unable to load this citation (code: FTFR). If this problem persists, please contact support.',
                  });
                } else {
                  openResourceDrawer({
                    model: resource,
                    telemetry,
                  });
                }
              });
            }
          },
        };
      },
      [
        summary.citations,
        summary.status,
        summaryResponse.status,
        summaryResponse.summaryJobId,
        summaryIndex,
        openResourceDrawer,
        telemetry,
        ctwFetch,
        patient,
        queryClient,
        graphqlClient,
        requestContext,
        fqsHotstorageRequestOptions,
      ],
    );

    return (
      <CollapsibleCard
        label={summary.title}
        controls={{ copyToClipboard: { content: summary.content } }}
        loading={!enabled}
      >
        <StreamingContent
          enabled={enabled}
          onAllContentPresented={onAllContentPresented}
          contentBlocks={contentBlocks}
          handleCitation={handleCitation}
          handleTextSelection={({ selectedCitationIds }) => {
            const selectedCitations = summary?.citations?.filter((citation) =>
              selectedCitationIds.includes(String(citation.id)),
            );
            telemetry.trackInteraction('patient_summary_select_narrative_text', {
              summaryJobId: summaryResponse.summaryJobId,
              summaryJobStatus: summaryResponse.status,
              summaryStatus: summary.status,
              summaryIndex,
              citationIds: selectedCitationIds,
              resourceTypes: selectedCitations?.map((citation) => citation.resourceType),
              resourceIds: selectedCitations?.map((citation) => citation.resourceId),
            });
          }}
        />
      </CollapsibleCard>
    );
  },
);
